import { Avatar, AvatarFallback, AvatarImage } from "./avatar"
import { cn } from "../../lib/utils"
import { useAuthStore } from "../../stores"

interface UserAvatarUser {
  display_name?: string
  avatar?: string
}

interface UserAvatarProps {
  /** User to display. If not provided, falls back to current authenticated user */
  user?: UserAvatarUser | string | null
  size?: "sm" | "md" | "lg"
  className?: string
}

const sizeConfig = {
  sm: {
    avatar: "h-7 w-7",
    text: "text-xs",
  },
  md: {
    avatar: "h-10 w-10",
    text: "text-sm",
  },
  lg: {
    avatar: "h-12 w-12",
    text: "text-base",
  },
}

function getInitials(name: string | undefined): string {
  if (!name) return "?"
  const parts = name.trim().split(/\s+/)
  if (parts.length === 1) {
    return parts[0].charAt(0).toUpperCase()
  }
  return (parts[0].charAt(0) + parts[parts.length - 1].charAt(0)).toUpperCase()
}

export function UserAvatar({ user, size = "md", className }: UserAvatarProps) {
  const authUser = useAuthStore((state) => state.user)
  const config = sizeConfig[size]

  // Determine avatar and display name
  let avatar: string | undefined
  let displayName: string | undefined

  if (user && typeof user === "object") {
    // User object provided
    avatar = user.avatar
    displayName = user.display_name
  } else if (!user) {
    // No user provided - fall back to current user
    avatar = authUser?.avatar
    displayName = authUser
      ? `${authUser.given_name || ""} ${authUser.family_name || ""}`.trim()
      : undefined
  }
  // If user is a string (ID), we can't resolve it - show fallback

  const initials = getInitials(displayName)

  return (
    <Avatar className={cn(config.avatar, className)}>
      <AvatarImage src={avatar} alt={displayName} />
      <AvatarFallback
        className={cn(config.text, "bg-primary text-primary-foreground font-medium")}
      >
        {initials}
      </AvatarFallback>
    </Avatar>
  )
}
